<template>
  <div id="gatewayTasks">
    <card :title="$t('g-tasks:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="ids" class="control-label">{{ $t('g-tasks:ids') | capitalize }}</label>
            <input id="ids"
                   v-model="filterIds"
                   v-validate="'text'"
                   :placeholder="$t('g-tasks:idsPlaceholder') | capitalize"
                   :data-vv-as="$t('g-tasks:ids')"
                   :class="{ 'is-invalid': errors.has('ids') }"
                   type="text"
                   name="ids"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('ids') }}</div>
          </div>

          <div class="form-group col-md-6">
            <label for="status" class="control-label">{{ $t('g-tasks:status') | capitalize }}</label>
            <select id="status" v-model="filterStatuses" class="form-control">
              <option value="" />
              <option v-for="status in tasksStatuses" :key="status.value" :value="status.value">{{ status.text }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="period" class="control-label">{{ $t('g-tasks:period') | capitalize }}</label>
            <flat-pickr id="period"
                        v-model="filterPeriod"
                        :config="fpConfig"
                        name="period"
                        class="form-control"
            />
            <div class="invalid-feedback">{{ errors.first('period') }}</div>
          </div>

          <div class="form-group col-md-6">
            <label for="action" class="control-label">{{ $t('g-tasks:method') | capitalize }}</label>
            <select id="action" v-model="filterMethods" class="form-control">
              <option value="" />
              <option v-for="action in tasksMethods" :key="action.value" :value="action.value">{{ action.text }}</option>
            </select>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('g-tasks:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="gatewayTasksGrid"
          :title="$t('g-tasks:tasks')"
          :title-small="$t('g-tasks:clickToSeeMessages')"
          :columns="tasksColumns"
          :paginated-dto="tasksRows"
          :enable-click-on-row="true"
          class="mb-3"
          @load="filter"
          @clickOnRow="onTaskClicked"
    >
      <template slot="status" slot-scope="props">
        <span :class="{'text-success': props.dto.status === 'OK', 'text-warning': props.dto.status === 'PENDING', 'text-danger': props.dto.status === 'KO'}">{{ props.dto.status }}</span>
      </template>

      <template slot="send-parameters" slot-scope="props">
        <a href @click.stop.prevent="openModalTaskSendParameters(props.dto)">
          {{ (props.dto.sendParameters.body ? props.dto.sendParameters.body : props.dto.sendParameters) | stringify | substring(50) }}
        </a>
      </template>
    </grid>

    <grid v-if="$store.getters.bscFeature(gateway, 'messageStep') && taskClicked"
          id="gatewayTasksMessagesGrid"
          :title="$t('g-tasks:messages')"
          :title-small="getTaskReference(taskClicked)"
          :columns="taskMessagesColumns"
          :paginated-dto="taskMessages"
          :pagination="false"
          :refresh-btn="false"
          class="mb-3"
    />

    <grid v-if="$store.getters.bscFeature(gateway, 'snmpLog') && taskClicked"
          id="gatewayTasksSnmpLogsGrid"
          :title="$t('g-tasks:snmp')"
          :title-small="getTaskReference(taskClicked)"
          :columns="taskSnmpLogsColumns"
          :paginated-dto="taskSnmpLogs"
          :pagination="false"
          :refresh-btn="false"
          :fixed="true"
    >
      <colgroup slot="colgroup"><col span="3"><col style="width: 65%;"></colgroup>

      <template slot="header-snmp-logs">
        {{ $t('g-tasks:content') | capitalize }}
        <span class="float-right">
          <input id="oid" v-model="oid" type="checkbox">
          <label for="oid">{{ $t('g-tasks:displayOidNames') | capitalize }}</label>
        </span>
      </template>

      <template slot="snmp-logs" slot-scope="props">
        <span v-if="!oid">{{ props.dto.content }}</span>
        <span v-else>{{ parseOid(props.dto.content) }}</span>
      </template>
    </grid>

    <modal id="modalTaskSendParameters"
           v-model="showModalTaskSendParameters"
           :title="titleModalTaskSendParameters"
           size="lg"
    >
      <div slot="modal-body">
        <!-- Need to use v-html to insert formatted json (secured content) -->
        <!-- eslint-disable-next-line -->
        <pre v-if="taskModalSendParameters" v-html="bodyModalTaskSendParameters" />
      </div>
    </modal>
  </div>
</template>

<script>
import { isEmpty, sortBy, isArray } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import formatDate from 'vue-kerlink/dist/filters/date'

import { tasks, tasksStatuses } from '@/configuration/tasks'

export default {
  titleKey: 'gatewayTasks',

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      // Filters
      filterIds: '',
      filterStatuses: [],
      filterPeriod: '',
      filterMethods: [],

      // Data
      tasksRows: {},
      showModalTaskSendParameters: false,
      taskModalSendParameters: null,

      // Clicked task messages and SNMP logs
      taskClicked: null,
      taskMessages: {},
      taskSnmpLogs: {},

      // Display oid name/code
      oid: false,

      // In data to be modified if needed
      fpConfig: {
        mode: 'range',
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true
      }
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    tasksMethods () { return tasks },
    tasksStatuses () { return tasksStatuses },

    tasksColumns () {
      return [
        { text: 'ID', id: 'id' },
        { text: this.$t('g-tasks:status'), slot: 'status' },
        { text: this.$t('g-tasks:date'), formatter: dto => formatDate(dto.creationDate) },
        { text: this.$t('g-tasks:method'), formatter: dto => dto.action.replace(/_/g, ' ') },
        { text: this.$t('g-tasks:criteria'), id: 'criteria' },
        { text: this.$t('g-tasks:errorCode'), id: 'errorCode' },
        { text: this.$t('g-tasks:errorMessage'), id: 'errorMessage' },
        { text: this.$t('g-tasks:sendParameters'), slot: 'send-parameters' }
      ]
    },

    taskMessagesColumns () {
      return [
        { text: 'ID', id: 'id' },
        { text: this.$t('g-tasks:date'), formatter: dto => formatDate(dto.date) },
        { text: this.$t('g-tasks:progressStatus'), formatter: dto => dto.step.replace(/_/g, ' ') },
        { text: this.$t('g-tasks:location'), id: 'location' },
        { text: this.$t('g-tasks:message'), id: 'message' },
        { text: this.$t('g-tasks:continu'), formatter: dto => { return dto.next ? this.$t('g-tasks:yes') : this.$t('g-tasks:no') } }
      ]
    },

    taskSnmpLogsColumns () {
      return [
        { text: 'ID', id: 'id' },
        { text: this.$t('g-tasks:origin'), formatter: dto => dto.origin.replace(/_/g, ' ') },
        { text: this.$t('g-tasks:date'), formatter: dto => formatDate(dto.createTimeStamp) },
        { headerSlot: 'header-snmp-logs', slot: 'snmp-logs' }
      ]
    },

    titleModalTaskSendParameters () {
      return this.taskModalSendParameters
        ? this.getTaskReference(this.taskModalSendParameters)
        : ''
    },

    bodyModalTaskSendParameters () {
      return this.$utils.highlight(
        this.taskModalSendParameters.sendParameters.body
          ? this.taskModalSendParameters.sendParameters.body
          : this.taskModalSendParameters.sendParameters
      )
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.tasksRows = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.tasksRows)) return

      this.filter()
    }
  },

  created () {
    if (this.$route.query && this.$route.query.taskId) this.filterIds = this.$route.query.taskId

    this.filter()
  },

  methods: {
    parseOid (str) { return this.$utils.parseOid(str) },

    getTaskReference (dto) {
      if (isEmpty(dto)) return ''

      return dto.id + ' - ' + dto.action.replace(/_/g, ' ') + ' - ' + dto.status + ' - ' + formatDate(dto.creationDate)
    },

    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      if (!isEmpty(this.gateway)) {
        options.eui = this.gateway.eui
        options.taskIds = this.filterIds

        if (this.filterStatuses) {
          options.status = this.filterStatuses
        }

        // Flatpickr return a string "<from> to <to>" with <from> and <to> dates in seconds
        const filterPeriod = this.filterPeriod ? this.filterPeriod.split(' to ').map(v => v * 1000) : []
        if (!isEmpty(filterPeriod)) {
          options.startDate = filterPeriod[0]
          options.endDate = filterPeriod[1]
        }

        if (this.filterMethods) {
          options.action = isArray(this.filterMethods) ? this.filterMethods.join(',') : this.filterMethods
        }

        // Hide Task and SNPM tables
        this.taskClicked = null

        this.getTasks(options)
      }
    },

    async getTasks (options) {
      const params = this.$utils.getParams({ eui: this.gateway.eui, taskIds: '', status: '', startDate: '', endDate: '', action: '', sort: '-date' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getTasks(this.$store.getters.application, params))
      if (error || !response) return

      this.tasksRows = response.data
    },

    openModalTaskSendParameters (dto) {
      this.taskModalSendParameters = dto
      this.showModalTaskSendParameters = true
    },

    onTaskClicked (dto) {
      this.getTaskMessages(dto)
      this.getTaskSnmpLogs(dto)
    },

    async getTaskMessages (dto) {
      if (!this.$store.getters.bscFeature(this.gateway, 'messageStep')) return

      let error, response
      [error, response] = await to(this.$api.getTaskMessages(dto))
      if (error || !response) return

      this.taskClicked = dto
      response.data.list = sortBy(response.data.list, 'date')
      this.taskMessages = response.data
    },

    async getTaskSnmpLogs (dto) {
      if (!this.$store.getters.bscFeature(this.gateway, 'snmpLog')) return

      let error, response
      [error, response] = await to(this.$api.getTaskSnmpLogs(dto))
      if (error || !response) return

      this.taskClicked = dto
      response.data.list = sortBy(response.data.list, 'createTimeStamp')
      this.taskSnmpLogs = response.data
    }
  }
}
</script>
